import storefront from "../global/storefront";
import { SHOWROOM_COLORS_QUERY } from "../global/storefront/queries";
import { escapeHtml } from "./showroom-api";

// `picker.value` is the selected color references (what the app stores);
// fires a bubbling `change` event on every selection change.

let paletteRequest = null;

function readJson(el, fallback) {
  if (!el) return fallback;

  try {
    return JSON.parse(el.textContent);
  } catch {
    return fallback;
  }
}

function normalizeColor(node) {
  const color = { id: node.id, handle: node.handle };

  node.fields.forEach((field) => {
    color[field.key] = field.key === "image" ? field.reference?.image?.url || null : field.value;
  });

  return color.label ? color : null;
}

// The palette is the same for every picker on the page, so it's fetched once.
function loadPalette(configEl) {
  if (paletteRequest) return paletteRequest;

  const configs = readJson(configEl, []);
  let ids = configs?.[0]?.show_colors || [];

  if (typeof ids === "string") ids = readJson({ textContent: ids }, []);

  if (!ids.length) {
    paletteRequest = Promise.resolve([]);
    return paletteRequest;
  }

  paletteRequest = storefront
    .request({ query: SHOWROOM_COLORS_QUERY, variables: { ids } })
    .then((response) => (response?.data?.nodes || []).filter(Boolean).map(normalizeColor).filter(Boolean));

  return paletteRequest;
}

class ShowroomColorPicker extends HTMLElement {
  async connectedCallback() {
    this.selectedList = this.querySelector("[data-selected-list]");
    this.options = this.querySelector("[data-options]");
    this.removeIcon = this.querySelector("template[data-remove-icon]")?.innerHTML || "&times;";
    this.initialSelection = readJson(this.querySelector("[data-showroom-color-selected]"), []);
    this.selected = [...this.initialSelection];

    this.selectedList?.addEventListener("click", (event) => {
      const remove = event.target.closest("[data-remove-color]");
      if (remove) this.toggle(remove.dataset.removeColor, false);
    });

    this.options?.addEventListener("click", (event) => {
      const option = event.target.closest("[data-color-reference]");
      if (!option) return;

      const reference = option.dataset.colorReference;
      this.toggle(reference, !this.value.includes(reference));
    });

    // A long selection scrolls sideways: let a mouse wheel scroll it too.
    this.selectedList?.addEventListener(
      "wheel",
      (event) => {
        const list = this.selectedList;
        if (list.scrollWidth <= list.clientWidth || Math.abs(event.deltaY) <= Math.abs(event.deltaX)) return;

        event.preventDefault();
        list.scrollLeft += event.deltaY;
      },
      { passive: false },
    );

    this.renderSelected();

    // Inside a closed modal (the create wizard is on every page), wait until
    // it opens before asking the Storefront API for the palette.
    const modal = this.closest("showroom-modal");
    if (modal && !modal.isOpen) {
      modal.addEventListener("showroom-modal:open", () => this.loadOptions(), { once: true });
    } else {
      this.loadOptions();
    }
  }

  async loadOptions() {
    this.palette = await loadPalette(this.querySelector("[data-showroom-color-config]"));
    this.renderOptions();
  }

  get value() {
    return this.selected.map((color) => color.reference);
  }

  get isDirty() {
    const initial = this.initialSelection.map((color) => color.reference).join(",");

    return initial !== this.value.join(",");
  }

  toggle(reference, select) {
    const isSelected = this.value.includes(reference);
    if (select === isSelected) return;

    if (select) {
      const color = this.palette?.find((entry) => entry.reference === reference);
      if (!color) return;

      this.selected.push({ reference: color.reference, label: color.label, image: color.image });
    } else {
      this.selected = this.selected.filter((color) => color.reference !== reference);
    }

    this.renderSelected();
    this.renderOptions();

    // Bring a newly added color into view at the end of the row.
    if (select) this.selectedList?.scrollTo({ left: this.selectedList.scrollWidth, behavior: "smooth" });

    this.dispatchEvent(new Event("change", { bubbles: true }));
  }

  renderSelected() {
    if (!this.selectedList) return;

    this.selectedList.hidden = this.selected.length === 0;
    this.selectedList.innerHTML = this.selected
      .map(
        (color) => `
          <div class="showroom-color-picker__chip" role="listitem">
            <img class="showroom-color-picker__chip-image" src="${escapeHtml(color.image)}" alt="" width="32" height="32" loading="lazy">
            <span class="showroom-color-picker__chip-label body">${escapeHtml(color.label)}</span>
            <button type="button" class="showroom-color-picker__chip-remove" data-remove-color="${escapeHtml(color.reference)}" aria-label="${escapeHtml(`${this.dataset.removeLabel || "Remove"} ${color.label}`)}">
              ${this.removeIcon}
            </button>
          </div>
        `,
      )
      .join("");
  }

  renderOptions() {
    if (!this.options || !this.palette) return;

    const selected = this.value;

    this.options.innerHTML = this.palette
      .map((color) => {
        const isSelected = selected.includes(color.reference);

        return `
          <button
            type="button"
            class="showroom-color-picker__option${isSelected ? " is-selected" : ""}"
            role="listitem"
            data-color-reference="${escapeHtml(color.reference)}"
            aria-pressed="${isSelected}"
            aria-label="${escapeHtml(color.label)}"
            title="${escapeHtml(color.label)}"
          >
            <img class="showroom-color-picker__option-image" src="${escapeHtml(color.image)}" alt="" width="56" height="56" loading="lazy">
          </button>
        `;
      })
      .join("");
  }
}

if (!customElements.get("showroom-color-picker")) {
  customElements.define("showroom-color-picker", ShowroomColorPicker);
}
